"use client";

import type React from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  Text,
  Flex,
  VStack,
  HStack,
  Badge,
  Divider,
} from "@chakra-ui/react";

interface Message {
  id: number;
  user_query: any;
  agent_response: any;
  updated_at: string;
}

interface WhatsAppDetailsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  conversation: {
    sender_id: string;
    receiver_id: string;
    agent_name: string;
    first_message_time: string;
    last_message_time: string;
    message_count: number;
    messages: Message[];
  } | null;
}

const extractContent = (jsonData: any): string => {
  try {
    if (typeof jsonData === "string") {
      const parsed = JSON.parse(jsonData);
      return parsed?.data?.content || parsed?.content || jsonData;
    }
    return (
      jsonData?.data?.content || jsonData?.content || JSON.stringify(jsonData)
    );
  } catch {
    return String(jsonData);
  }
};

export const WhatsAppDetailsDialog: React.FC<WhatsAppDetailsDialogProps> = ({
  isOpen,
  onClose,
  conversation,
}) => {
  if (!conversation) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent bg={"white"} maxH="90vh">
        <ModalHeader borderBottom="1px solid" borderColor={"gray.200"}>
          <VStack align="stretch" spacing={2}>
            <Text fontSize="xl" fontWeight="bold">
              WhatsApp Conversation Details
            </Text>
            <HStack spacing={4} fontSize="sm" color={"gray.600"}>
              <Text>
                <strong>Lead:</strong> {conversation.sender_id}
              </Text>
              <Text>
                <strong>Agent:</strong> {conversation.receiver_id}
              </Text>
              <Text>
                <strong>Agent Name:</strong> {conversation.agent_name}
              </Text>
            </HStack>
            <HStack spacing={4} fontSize="sm" color={"gray.600"}>
              <Text>
                <strong>Duration:</strong>{" "}
                {new Date(conversation.first_message_time).toLocaleString()} -{" "}
                {new Date(conversation.last_message_time).toLocaleString()}
              </Text>
              <Badge colorScheme="green">
                {conversation.message_count} messages
              </Badge>
            </HStack>
          </VStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody py={6}>
          <VStack align="stretch" spacing={4}>
            <Text fontSize="lg" fontWeight="semibold">
              Conversation
            </Text>
            <Divider />
            <VStack align="stretch" spacing={3} maxH="60vh" overflowY="auto">
              {conversation.messages.map((msg, idx) => (
                <Box key={msg.id || idx}>
                  {/* User Message */}
                  <Flex justify="flex-start" mb={2}>
                    <Box
                      maxW="70%"
                      bg={"blue.50"}
                      borderRadius="lg"
                      p={3}
                      borderLeft="4px solid"
                      borderColor="blue.500"
                    >
                      <HStack mb={1}>
                        <Badge colorScheme="blue" fontSize="xs">
                          Lead
                        </Badge>
                        <Text fontSize="xs" color={"gray.500"}>
                          {new Date(msg.updated_at).toLocaleTimeString()}
                        </Text>
                      </HStack>
                      <Text fontSize="sm">
                        {extractContent(msg.user_query)}
                      </Text>
                    </Box>
                  </Flex>

                  {/* Agent Message */}
                  <Flex justify="flex-end">
                    <Box
                      maxW="70%"
                      bg={"green.50"}
                      borderRadius="lg"
                      p={3}
                      borderRight="4px solid"
                      borderColor="green.500"
                    >
                      <HStack mb={1} justify="flex-end">
                        <Text fontSize="xs" color={"gray.500"}>
                          {new Date(msg.updated_at).toLocaleTimeString()}
                        </Text>
                        <Badge colorScheme="green" fontSize="xs">
                          Agent
                        </Badge>
                      </HStack>
                      <Text fontSize="sm">
                        {extractContent(msg.agent_response)}
                      </Text>
                    </Box>
                  </Flex>
                </Box>
              ))}
            </VStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
